import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { deleteFile, postFile } from 'features/files/fileAPI';
import { FolderLocation, getStorageFolderName } from 'features/files/fileSlice';
import { ISiteState } from 'features/site/siteSlice';
import { toAsciiId } from 'helpers/helpers';
import {
  deletePublicationData,
  fetchPublications,
  IPublication,
  patchPublicationData,
  postPublicationData,
} from './publicationAPI';

export interface IPublicationsState {
  publications: IPublication[];
  status: 'idle' | 'loading' | 'failed';
  errors: any;
}

export type PublicationsStateProps = {
  publications: IPublication[];
};

export const initialState: IPublicationsState = {
  publications: [],
  status: 'idle',
  errors: '',
};

export function getPublicationAnchorId(
  publication: IPublication,
  useTitle = true,
) {
  return useTitle
    ? toAsciiId(publication.title ?? '') || `publication-${publication.id}`
    : `publication-${publication.id}`;
}

export function getPublicationCitation(publication: IPublication) {
  const lines = publication.text?.split('\n');
  if (!lines) return '';
  const paragraphLines: string[] = [];
  for (const line of lines) {
    if (line.trim() === '') {
      break;
    }
    paragraphLines.push(line);
  }
  return paragraphLines.join('\n');
}
export function sortPublicationsByDate(publications: IPublication[]) {
  const sortedPublications = [...publications];
  return sortedPublications.sort((a, b) => {
    const dateA = new Date(a.publicationDate!).getTime();
    const dateB = new Date(b.publicationDate!).getTime();
    return dateB - dateA;
  });
}

export const fetchPublicationsAsync = createAsyncThunk(
  'publication/fetchPublications',
  async () => {
    const res = await fetchPublications();
    return { response: res };
  },
);

export const postPublicationAsync = createAsyncThunk(
  'publication/postPublication',
  async (
    {
      data,
    }: {
      data: IPublication;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await postPublicationData(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchPublicationAsync = createAsyncThunk(
  'publication/patchPublication',
  async (
    {
      data,
      file,
      publication,
    }: {
      data: IPublication;
      file?: File;
      publication?: IPublication;
    },
    thunkAPI,
  ) => {
    try {
      if (file || !data.image) {
        if (publication?.image) {
          if (typeof publication.image === 'object')
            await deleteFile(publication.image.id);
          else await deleteFile(publication.image);
        }
      }
      if (file) {
        const fd = await postFile(file, {
          description: 'Image for publication ' + data.title,
          storage: getStorageFolderName(FolderLocation.Images),
          size: file.size,
          mimeType: file.type,
        });
        data.image = { id: fd.data._id, uri: fd.data.message };
      }
      return { response: await patchPublicationData(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const deletePublicationAsync = createAsyncThunk(
  'publication/deletePublication',
  async (
    {
      id,
    }: {
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await deletePublicationData(id) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export function addPublicationCases(
  builder: ActionReducerMapBuilder<ISiteState>,
) {
  builder
    .addCase(fetchPublicationsAsync.pending, (state) => {
      state.status = 'loading';
    })
    .addCase(fetchPublicationsAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      state.publications = action.payload.response.data;
    })
    .addCase(fetchPublicationsAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(postPublicationAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(postPublicationAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const id = action.payload?.response?.data?._id;
      if (id) {
        const data = { ...action.meta.arg.data, id };
        state.publications.push(data);
      }
    })
    .addCase(postPublicationAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(patchPublicationAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(patchPublicationAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const publication = state.publications.find(
        (publication) => publication.id === action.meta.arg.data.id,
      );
      if (publication) {
        Object.assign(publication, action.meta.arg.data);
      }
    })
    .addCase(patchPublicationAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(deletePublicationAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(deletePublicationAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      state.publications = state.publications.filter(
        (publication) => publication.id !== action.meta.arg.id,
      );
    })
    .addCase(deletePublicationAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    });
}
